import React from 'react';
import { useTranslation } from 'react-i18next';
import sanitizeHtml from 'sanitize-html';

import CancelRoundedIcon from '@mui/icons-material/CancelRounded';
import CheckCircleRoundedIcon from '@mui/icons-material/CheckCircleRounded';
import ErrorRoundedIcon from '@mui/icons-material/ErrorRounded';
import WarningRoundedIcon from '@mui/icons-material/WarningRounded';
import {
  FEATURE,
  PACKAGE_CHANGE_TYPES,
  TYPE_POPUP_MIGRATE,
} from '@src/constants/package';
import { Typography } from '@mui/material';
import {
  checkCustomPackage,
  checkFreePackage,
  getPackageChangeType,
} from '@src/services/package';
import { useSelector } from 'react-redux';

const Content = ({
  usingPackage,
  packageState,
  creditsOrderData,
  typeCheckUpDownGradePackV3,
}) => {
  const { t, i18n } = useTranslation();
  const { language } = i18n;
  const { user = {} } = useSelector((state) => state.auth);

  const packageChangeType = getPackageChangeType({
    currPackage: packageState,
    prevPackage: usingPackage,
    prevPackageExpiredDate: user?.packageExpiryDate,
  });

  const isDowngrade = packageChangeType === PACKAGE_CHANGE_TYPES.DOWNGRADE;
  const isUpgrade = packageChangeType === PACKAGE_CHANGE_TYPES.UPGRADE;
  const isCustomPackage = checkCustomPackage({
    level: usingPackage.level,
    type: usingPackage.type,
  });
  const isBuyingCustomPackage = checkCustomPackage({
    level: packageState.level,
    type: packageState.type,
  });

  const creditsTransferred = creditsOrderData?.creditsTransferred || 0;
  const totalCreditsCycle = creditsOrderData?.totalCreditsCycle || 0;

  const isFreePackage = checkFreePackage(user.packageCode);
  const oneTimeCredits = isFreePackage
    ? user?.lockCharacters
    : user?.remainingCharacters + user?.bonusCharacters || user?.lockCharacters;

  const getVoiceLevelContent = (currPkg, buyPkg) => {
    const isCurrPkgHasBasicGlobalVoice = currPkg?.features?.includes(
      FEATURE.BASIC_GLOBAL_VOICE,
    );
    const isCurrPkgHasAdvanceGlobalVoice = currPkg?.features?.includes(
      FEATURE.ADVANCED_GLOBAL_VOICE,
    );
    const isNewPkgHasBasicGlobalVoice = buyPkg?.features?.includes(
      FEATURE.BASIC_GLOBAL_VOICE,
    );
    const isNewPkgHasAdvanceGlobalVoice = buyPkg?.features?.includes(
      FEATURE.ADVANCED_GLOBAL_VOICE,
    );

    let voiceLevelContent = '';
    let voiceLevelStatus = 'error';

    const isNotSupportAdvancedGlobalVoice =
      isCurrPkgHasAdvanceGlobalVoice &&
      !isNewPkgHasAdvanceGlobalVoice &&
      isNewPkgHasBasicGlobalVoice;

    const isNotSupportBasicAndAdvanceGlobalVoice =
      isCurrPkgHasAdvanceGlobalVoice &&
      !isNewPkgHasAdvanceGlobalVoice &&
      !isNewPkgHasBasicGlobalVoice;

    const isNotSupportBasicGlobalVoice =
      isCurrPkgHasBasicGlobalVoice && !isNewPkgHasBasicGlobalVoice;

    const notChange =
      !isCurrPkgHasBasicGlobalVoice && !isNewPkgHasBasicGlobalVoice;
    if (notChange) return null;

    if (isNotSupportAdvancedGlobalVoice) {
      voiceLevelContent = t('notSupportAdvancedGlobalVoiceV2');
      voiceLevelStatus = 'error';
    } else if (isNotSupportBasicAndAdvanceGlobalVoice) {
      voiceLevelContent = t('notSupportBasicAndAdvancedGlobalVoiceV2');
      voiceLevelStatus = 'error';
    } else if (isNotSupportBasicGlobalVoice) {
      voiceLevelContent = t('notSupportBasicGlobalVoiceV2');
      voiceLevelStatus = 'error';
    }
    if (!voiceLevelContent) return null;

    return { content: voiceLevelContent, status: voiceLevelStatus };
  };

  const getVoiceCloningContent = (currPkg, buyPkg) => {
    // Extract voice cloning properties
    const voiceCloning = {
      current: {
        hasFeature: currPkg?.features?.includes(FEATURE.AI_VOICE_CLONING),
        voices: currPkg?.maxCloningVoices || 0,
      },
      new: {
        hasFeature: buyPkg?.features?.includes(FEATURE.AI_VOICE_CLONING),
        voices: buyPkg?.maxCloningVoices || 0,
      },
    };

    // Handle case where feature is being removed
    if (voiceCloning.current.hasFeature && !voiceCloning.new.hasFeature) {
      return {
        content: t('noAccessToVoiceCloningAIV2'),
        status: 'error',
      };
    }

    return null;
  };

  const getQuotaChangeQuotaMultiCycleContent = (buyPkg) => {
    const totalCycles = buyPkg?.cycle?.totalCycles || 0;
    const remainingCredits = creditsTransferred - totalCreditsCycle;

    if (!isDowngrade && creditsTransferred > 0) {
      if (isCustomPackage && creditsTransferred > 0 && !isBuyingCustomPackage) {
        return {
          content: t(
            'creditsRolloverAreForwardedAndEvenlyDividedAcrossAllCycles',
            {
              credits: creditsTransferred.toLocaleString(language),
            },
          ),
          status: 'success',
        };
      }
      if (!isCustomPackage && totalCycles > 1 && remainingCredits > 0) {
        return {
          content: t('creditAreForwardedAndEvenlyDividedAcrossAllCycles', {
            credits: remainingCredits.toLocaleString(language),
          }),
          status: 'success',
        };
      }
    }
    return null;
  };

  const getLimitCreditsPerMonthContent = (buyPkg) => {
    const totalCycles = buyPkg?.cycle?.totalCycles || 0;
    if (totalCycles > 1 && isCustomPackage) {
      return {
        content: t('limitCreditsPerMonth'),
        status: 'error',
      };
    }
    return null;
  };

  const getQuotaChangeContent = (buyPkg) => {
    let quotaChangeQuotaSingleCycleContent = '';
    let quotaChangeQuotaSingleCycleStatus = 'error';

    if (isDowngrade) {
      quotaChangeQuotaSingleCycleContent = t('amountOfCharactersWillBeLost', {
        credits: creditsOrderData?.creditsLost?.toLocaleString(language),
      });
      quotaChangeQuotaSingleCycleStatus = 'error';
    }

    if (
      (isUpgrade && buyPkg?.cycle) ||
      isBuyingCustomPackage ||
      packageState?.code === usingPackage?.code
    ) {
      const totalCycles = buyPkg?.cycle?.totalCycles || 0;

      if (
        (totalCycles === 1 || isBuyingCustomPackage) &&
        creditsTransferred > 0
      ) {
        quotaChangeQuotaSingleCycleContent = t(
          'creditsWillBeAddedYourNewPlan',
          {
            credits: creditsTransferred.toLocaleString(language),
          },
        );
        quotaChangeQuotaSingleCycleStatus = 'success';
      } else if (totalCycles > 1 && totalCreditsCycle > 0) {
        quotaChangeQuotaSingleCycleContent = t('creditsWillBeAddedFirstCycle', {
          credits: totalCreditsCycle.toLocaleString(language),
        });
        quotaChangeQuotaSingleCycleStatus = 'success';
      }
    }

    return {
      content: quotaChangeQuotaSingleCycleContent,
      status: quotaChangeQuotaSingleCycleStatus,
    };
  };

  const getQuotaOneTimeCreditsContent = () => {
    if (oneTimeCredits) {
      return {
        content: t('certainBenefitsOfTheOldPlanWillRemain', {
          credits: oneTimeCredits.toLocaleString(language),
        }),
        status: 'success',
      };
    }
    return null;
  };

  const getPackageChangeContent = (currPkg, buyPkg) => {
    const contents = [];

    const quotaSingleCycleContent = getQuotaChangeContent(buyPkg);
    if (quotaSingleCycleContent) contents.push(quotaSingleCycleContent);

    const voiceLevelContent = getVoiceLevelContent(currPkg, buyPkg);
    if (voiceLevelContent) contents.push(voiceLevelContent);

    const voiceCloningAIContent = getVoiceCloningContent(currPkg, buyPkg);
    if (voiceCloningAIContent) contents.push(voiceCloningAIContent);

    const limitCreditsPerMonthContent = getLimitCreditsPerMonthContent(buyPkg);
    if (limitCreditsPerMonthContent) contents.push(limitCreditsPerMonthContent);

    const customPackageQuotaMultiCycleContent =
      getQuotaChangeQuotaMultiCycleContent(buyPkg);
    if (customPackageQuotaMultiCycleContent)
      contents.push(customPackageQuotaMultiCycleContent);

    const quotaOneTimeCreditsContent = getQuotaOneTimeCreditsContent();
    if (quotaOneTimeCreditsContent) contents.push(quotaOneTimeCreditsContent);

    return contents;
  };

  const changePackageContents = getPackageChangeContent(
    usingPackage,
    packageState,
  );

  const renderStatusIcon = (status) => {
    switch (status) {
      case 'success':
        return <CheckCircleRoundedIcon className="icon-check-circle" />;
      case 'error':
        return <CancelRoundedIcon className="icon-cancel-circle" />;
      case 'warning':
        return <ErrorRoundedIcon className="icon-error-circle" />;
      default:
        return <CheckCircleRoundedIcon className="icon-error-circle" />;
    }
  };

  const renderWarningNote = () => {
    if (
      isDowngrade === true ||
      typeCheckUpDownGradePackV3 === TYPE_POPUP_MIGRATE.TYPE_MIGRATE_V1_V2_TO_V3
    )
      return null;

    return (
      <>
        {oneTimeCredits > 0 ? (
          <div className="note-warning-lock-circle">
            <WarningRoundedIcon className="icon-warning-lock-circle" />
            <div className="contents">
              <Typography
                component="span"
                className="text-note"
                dangerouslySetInnerHTML={{
                  __html: sanitizeHtml(
                    t('noteWarningPackageHasOneTimeCredits', {
                      credits: oneTimeCredits.toLocaleString(language),
                    }),
                  ),
                }}
              />
            </div>
          </div>
        ) : (
          <div className="note-warning-lock-circle">
            <WarningRoundedIcon className="icon-warning-lock-circle" />
            <div className="contents">
              <Typography
                component="span"
                className="text-note"
                dangerouslySetInnerHTML={{
                  __html: sanitizeHtml(t('noteWarningLockCycleV2')),
                }}
              />
            </div>
          </div>
        )}
      </>
    );
  };

  return (
    <div className="list">
      {changePackageContents
        ?.filter((item) => item?.content)
        ?.map((item) => (
          <div key={item?.key} className="item">
            {renderStatusIcon(item?.status)}
            <Typography
              component="span"
              dangerouslySetInnerHTML={{
                __html: item?.content,
              }}
            />
          </div>
        ))}
      {renderWarningNote()}
    </div>
  );
};

export default Content;
